import { cn } from '@nextui-org/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ErrorIconCross } from '~/components/icons'
import { checkEmailBlocked, getBlockedEmailDomains } from '~/services/userService'

interface EmailInputProps {
  id?: string
  placeholder?: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  className?: string
  autoComplete?: string
  autoFocus?: boolean
  disabled?: boolean
  error?: string
  onValidationChange?: (isValid: boolean) => void
  height?: 'sm' | 'md' | 'lg'
}

// Shared error messages -- single source of truth for validation strings
const EMAIL_ERRORS = {
  INVALID_FORMAT: 'Invalid email address',
  UNSUPPORTED_DOMAIN: 'Unsupported Domain. Please use your company email.'
} as const

// Default list of blocked email domains (fallback if API call fails)
// This list should match the server-side BLOCKED_EMAIL_DOMAINS config
const DEFAULT_BLOCKED_DOMAINS: string[] = [
  'yokemail.com',
  'forkemail.com',
  'tempmail.com',
  'tempmail.io',
  'temp-mail.org',
  'temp-mail.com',
  'guerrillamail.com',
  'guerrillamail.biz',
  '10minutemail.com',
  '10minutemail.net',
  '10minutemail.org',
  'throwaway.email',
  'throwawaymail.com',
  'mailinator.com',
  'mailinator.net',
  'mailinator2.com',
  'sharklasers.com',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'discardmail.com',
  'discardmail.de',
  'jetable.com',
  'jetable.net',
  'mailcatch.com',
  'maildrop.cc',
  'binkmail.com',
  'anonbox.net',
  'fakeinbox.com',
  'moonfee.com',
  'protonza.com',
  'passmail.com',
  'gddcorp.com',
  'iffincrane.com',
  'dropeso.com',
  '0cd.cn',
  'fixwap.com',
  'taypop.com',
  'gta5hx.com',
  'gmial.com',
  'haotuwu.com',
  'usap2.com',
  'gnail.com',
  'datoinf.com',
  'memeazon.com',
  'djkux.com',
  'fintehs.com',
  'bllibl.com',
  'waosu.com',
  'bitmens.com',
  'rograc.com'
]

export function EmailInput({
  id,
  placeholder = 'Email',
  value,
  onChange,
  onBlur,
  className = '',
  autoComplete = 'email',
  autoFocus = false,
  disabled = false,
  error,
  onValidationChange,
  height = 'md'
}: EmailInputProps) {
  const [isFocused, setIsFocused] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [blockedDomains, setBlockedDomains] = useState<string[]>(DEFAULT_BLOCKED_DOMAINS)
  // Start with domains loaded as true since we have defaults
  const [domainsLoaded, setDomainsLoaded] = useState(true)

  useEffect(() => {
    const fetchBlockedDomains = async () => {
      try {
        const domains = await getBlockedEmailDomains()
        setBlockedDomains(domains)
        setDomainsLoaded(true)
      } catch (error) {
        console.warn('Failed to fetch blocked domains, using defaults:', error)
        // Keep using DEFAULT_BLOCKED_DOMAINS which are already set
        setDomainsLoaded(true)
      }
    }

    fetchBlockedDomains()
  }, [])

  // Debounce timer for server-side whitelist check
  const whitelistCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Cache of emails confirmed as whitelisted by the server
  const whitelistedCache = useRef<Set<string>>(new Set())

  // Server-side whitelist check, debounced to avoid excessive API calls.
  // Called when client-side check flags a blocked domain during typing.
  const checkWhitelistDebounced = useCallback(
    (email: string) => {
      if (whitelistCheckTimer.current) {
        clearTimeout(whitelistCheckTimer.current)
      }
      whitelistCheckTimer.current = setTimeout(async () => {
        try {
          const result = await checkEmailBlocked(email)
          if (!result.blocked) {
            // Server says not blocked (whitelisted) - cache and clear
            whitelistedCache.current.add(email.toLowerCase())
            setHasError(false)
            setErrorMessage('')
            onValidationChange?.(true)
          } else {
            // Server confirmed blocked - now show the error
            setHasError(true)
            setErrorMessage(result.reason || EMAIL_ERRORS.UNSUPPORTED_DOMAIN)
          }
        } catch {
          // Server unreachable - show error as fallback
          setHasError(true)
          setErrorMessage(EMAIL_ERRORS.UNSUPPORTED_DOMAIN)
        }
      }, 300)
    },
    [onValidationChange]
  )

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (whitelistCheckTimer.current) {
        clearTimeout(whitelistCheckTimer.current)
      }
    }
  }, [])

  // Validates email format and blocked domains. Runs on every change
  // so the submit button disables immediately. If a blocked domain is
  // detected, also kicks off a debounced server check for whitelist override.
  const validateEmail = (email: string) => {
    if (!email) {
      setHasError(false)
      setErrorMessage('')
      onValidationChange?.(true)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!emailRegex.test(email)) {
      setHasError(true)
      setErrorMessage(EMAIL_ERRORS.INVALID_FORMAT)
      onValidationChange?.(false)
      return
    }

    if (domainsLoaded) {
      const domain = email.split('@')[1]?.toLowerCase()
      if (domain && blockedDomains.includes(domain)) {
        // Skip the block if this email was already confirmed whitelisted
        if (whitelistedCache.current.has(email.toLowerCase())) {
          setHasError(false)
          setErrorMessage('')
          onValidationChange?.(true)
          return
        }
        // Disable submit but don't show error yet - wait for server confirmation
        setHasError(false)
        setErrorMessage('')
        onValidationChange?.(false)
        // Server check will show error if confirmed blocked, or re-enable if whitelisted
        checkWhitelistDebounced(email)
        return
      }
    }

    setHasError(false)
    setErrorMessage('')
    onValidationChange?.(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e)
    validateEmail(e.target.value)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    validateEmail(e.target.value)
    onBlur?.(e)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const getHeightStyles = () => {
    switch (height) {
      case 'sm':
        return 'h-8 px-4 py-1.5'
      case 'md':
        return 'h-10 px-6 py-2.5'
      case 'lg':
        return 'h-12 px-6 py-3'
      default:
        return 'h-10 px-6 py-2.5'
    }
  }

  const displayError = error || (hasError ? errorMessage : '')

  return (
    <div className='w-full'>
      <div className='relative'>
        <input
          id={id}
          className={cn(
            // Base styles
            'w-full rounded-xl',
            'text-sm font-["Geist"] leading-tight tracking-tight',
            getHeightStyles(),

            // Border styles
            'border transition-colors duration-200',

            // Text and placeholder colors
            'text-neutral-800',
            'placeholder:text-neutral-400',

            // Background and states
            'bg-white',
            'hover:shadow transition-shadow duration-200',

            // Focus state
            isFocused && !hasError
              ? 'focus:shadow focus:outline-none focus:border-[#846FE1]'
              : 'focus:outline-none',

            // Error state
            hasError || displayError ? 'border-red-500 focus:border-red-500' : 'border-gray-300',

            // Disabled state
            'disabled:bg-surface/10',
            'disabled:border-surface/10',
            'disabled:text-neutral-400',
            'disabled:cursor-not-allowed',

            // Right padding for the error icon inside the input
            displayError ? 'pr-10' : 'pr-10',

            className
          )}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
          type='email'
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={value}
          autoComplete={autoComplete}
        />
        {/* Error icon inside the input */}
        {(hasError || displayError) && (
          <div className='absolute inset-y-0 right-0 flex items-center pr-4 select-none'>
            <ErrorIconCross />
          </div>
        )}
      </div>

      {/* Error message below the input -- always rendered to reserve space */}
      <div className='flex items-center gap-1 mt-1 px-1 min-h-[20px]'>
        {displayError && (
          <span className='text-red-600 text-sm' aria-label='Email input error message'>
            {displayError}
          </span>
        )}
      </div>
    </div>
  )
}
